import { create } from 'zustand';

import type { FileDto } from '@/api/api.dto';
import { getServiceId, setServiceId } from '@/api/tokens.helper';
import { useActiveAccount } from '@/store/active-account/active-account.store';
import { useAuth } from '@/store/auth/auth.store';
import { usePerson } from '@/store/persons/person.store';
import { runWithRetry } from '@/utils/runWithRetry';

import {
  checkVesselEmailExists,
  createActivityGenerator,
  createDailyLog,
  createMaintenance,
  createVessel,
  deleteActivityGenerator,
  deleteDailyLog,
  deleteVessel,
  editDailyLog,
  ensureMaintenanceId,
  getDailyLogByDate,
  getDailyLogPdf,
  getDailyLogs,
  getEquipment,
  getMaintenances,
  getMyVessels,
  getSingleActivityGenerator,
  getVesselsByIds,
  setVesselImage,
  signDailyLog,
  updateActivityGenerator,
  updateVessel,
} from './vessel.api';
import type { DeleteActivityMode } from './vessel.api';
import type {
  ActivityGenerator,
  CreateActivityGeneratorPayload,
  CreateVesselPayload,
  DailyLog,
  Equipment,
  UpdateVesselDto,
  Vessel,
} from './vessel.dto';

type VesselState = {
  vessel?: Vessel;
  // vessels owned by the current user
  ownedVessels?: Vessel[];
  // all vessels accessible by the current user
  myVessels?: Vessel[];
  createVessel: (payload: CreateVesselPayload) => Promise<Vessel>;
  updateVessel: (payload: UpdateVesselDto, vesselId?: string) => Promise<Vessel>;
  getOwnedVessels: (ownerId: string) => void;
  getMyVessels: (vesselIds: string[]) => Promise<Vessel[]>;
  selectVessel: (vesselId: string) => void;
  setVesselImage: (imageFile: File, vesselId?: string) => Promise<FileDto>;
  getEquipment: () => Promise<Equipment[]>;
  checkVesselEmailExists: (email: string) => Promise<boolean>;
  deleteVessel: (vesselId: string) => Promise<void>;
  deleteActivityGenerator: (
    generatorId: string,
    mode?: DeleteActivityMode,
    itemIds?: string[],
  ) => Promise<void>;
  updateActivityGenerator: (payload: ActivityGenerator) => Promise<ActivityGenerator>;
  getSingleActivityGenerator: (generatorId: string) => Promise<ActivityGenerator>;
  createActivityGenerator: (
    payload: CreateActivityGeneratorPayload,
  ) => Promise<{ id: string } & CreateActivityGeneratorPayload>;
  getMaintenances: () => Promise<{ id: string; title: string; vessel_id: string }[]>;
  ensureMaintenanceId: () => Promise<string>;
  createDailyLog: (log: DailyLog) => Promise<DailyLog>;
  editDailyLog: (log: DailyLog, id: string) => Promise<void>;
  deleteDailyLog: (logId: string) => Promise<void>;
  getDailyLogs: () => Promise<DailyLog[]>;
  getDailyLogByDate: (date: string) => Promise<DailyLog | undefined>;
  getDailyLogPdf: (logId: string, timeZone?: string) => Promise<Blob>;
  signDailyLog: (logId: string) => Promise<void>;
};

export const useVessels = create<VesselState>((set, get) => ({
  createVessel: async ({ created_as, ...payload }: CreateVesselPayload) => {
    const vessel = await createVessel({
      ...payload,
      image: null, // Image is uploaded separately
      create_as: created_as,
    });
    set((state) => ({
      myVessels: state.myVessels ? [...state.myVessels, vessel] : [vessel],
    }));
    useActiveAccount.getState().setActiveAccount(vessel.id, 'vessel');

    // The vessel exists by now whatever happens to its photo; a failed upload says so, with Retry.
    const image = payload.image;
    if (image) {
      await runWithRetry(() => get().setVesselImage(image, vessel.id));
    }

    // Awaited rather than fired and forgotten: this is the only place a vessel gets the
    // maintenance plan that every repeatable task has to be filed under, and an unawaited
    // call fails silently — a 403 while the new vessel's role is still propagating, or the
    // user navigating away right after create, and the vessel is planless for good with
    // nothing in the log. Still not fatal: the first repeatable task creates the plan
    // lazily through ensureMaintenanceId.
    try {
      await createMaintenance({
        title: `${vessel.vessel_name} Initial Maintenance`,
        vessel_id: vessel.id,
      });
    } catch (error) {
      console.error('Failed to create initial maintenance:', error);
    }

    // Refresh session and person store to pick up the role for the new vessel
    // (usePerson.me() updates currentUser.services, which triggers getMyVessels in MyYachts)
    await useAuth.getState().me();
    await usePerson.getState().me();

    // normalizeSession may overwrite SERVICE_ID_KEY with the first session service if the
    // backend hasn't yet propagated the new vessel role. Restore the intended active state.
    set((state) => ({
      myVessels: state.myVessels?.some((v) => v.id === vessel.id)
        ? state.myVessels
        : [...(state.myVessels ?? []), vessel],
    }));
    useActiveAccount.getState().setActiveAccount(vessel.id, 'vessel');

    return vessel;
  },
  updateVessel: async (payload: UpdateVesselDto, vesselId?: string) => {
    const response = await updateVessel(payload, vesselId);
    const updatedVessel =
      payload.questions && !response.questions
        ? { ...response, questions: payload.questions }
        : response;
    set((state) => ({
      myVessels: state.myVessels?.map((v) => (v.id === updatedVessel.id ? updatedVessel : v)),
      ownedVessels: state.ownedVessels?.map((v) => (v.id === updatedVessel.id ? updatedVessel : v)),
    }));
    return updatedVessel;
  },
  getOwnedVessels: async (ownerId: string) => {
    const vessels = await getMyVessels(ownerId);
    set({ ownedVessels: vessels });

    const currentServiceId = getServiceId();
    const activeVessel = currentServiceId ? vessels.find((v) => v.id === currentServiceId) : null;
    const selectedVessel = activeVessel || vessels[0];

    if (selectedVessel) {
      useActiveAccount.getState().setActiveAccount(selectedVessel.id, 'vessel');
    }
  },
  getMyVessels: async (vesselIds: string[]) => {
    if (!vesselIds.length) {
      set({ myVessels: [] });
      return [];
    }

    const isServiceProviderActive = useActiveAccount.getState().serviceType === 'service-provider';

    // Fix Race Condition & Stale ID:
    // If Service ID is missing OR invalid (not in the user's list), set a valid one.
    if (!isServiceProviderActive) {
      const currentId = getServiceId();
      const isValidId = currentId && vesselIds.includes(currentId);

      if (!isValidId) {
        setServiceId(vesselIds[0]);
      }
    }

    const vessels = await getVesselsByIds(vesselIds);
    set({ myVessels: vessels });

    if (isServiceProviderActive) {
      return vessels;
    }

    const currentServiceId = getServiceId();

    const activeVessel = currentServiceId ? vessels.find((v) => v.id === currentServiceId) : null;
    const selectedVessel = activeVessel || vessels[0];

    if (selectedVessel) {
      useActiveAccount.getState().setActiveAccount(selectedVessel.id, 'vessel');
    }

    return vessels;
  },
  selectVessel: (vesselId: string) => {
    const selectedVessel = useVessels.getState().myVessels?.find((v) => v.id === vesselId);

    if (selectedVessel) {
      useActiveAccount.getState().setActiveAccount(vesselId, 'vessel');
    }
  },
  setVesselImage: async (imageFile: File, vesselId?: string) => {
    const image = await setVesselImage(imageFile, vesselId);

    const state = useVessels.getState();
    const target = vesselId
      ? (state.myVessels?.find((v) => v.id === vesselId) ??
        state.ownedVessels?.find((v) => v.id === vesselId))
      : state.vessel;
    if (target) {
      const updatedVessel = { ...target, image };
      set((state) => ({
        myVessels: state.myVessels?.map((v) => (v.id === updatedVessel.id ? updatedVessel : v)),
        ownedVessels: state.ownedVessels?.map((v) =>
          v.id === updatedVessel.id ? updatedVessel : v,
        ),
      }));
    }

    return image;
  },
  getEquipment: async () => {
    const equipment = await getEquipment();
    return equipment;
  },
  checkVesselEmailExists: async (email: string) => {
    return await checkVesselEmailExists(email);
  },
  deleteVessel: async (vesselId: string) => {
    await deleteVessel(vesselId);

    const state = useVessels.getState();
    const updatedVessels = state.myVessels?.filter((v) => v.id !== vesselId);
    const newSelectedVessel = updatedVessels?.length ? updatedVessels[0] : undefined;

    set({ myVessels: updatedVessels });

    if (newSelectedVessel) {
      useActiveAccount.getState().setActiveAccount(newSelectedVessel.id, 'vessel');
    } else {
      useActiveAccount.getState().clearActiveAccount();
    }
  },
  getSingleActivityGenerator: async (generatorId: string) => {
    return await getSingleActivityGenerator(generatorId);
  },
  createActivityGenerator: async (payload: CreateActivityGeneratorPayload) => {
    return await createActivityGenerator(payload);
  },
  updateActivityGenerator: async (payload: ActivityGenerator): Promise<ActivityGenerator> => {
    const generator = await updateActivityGenerator(payload);
    return generator;
  },
  deleteActivityGenerator: async (generatorId, mode, itemIds) => {
    await deleteActivityGenerator(generatorId, mode, itemIds);
  },
  getMaintenances: async () => {
    const response = await getMaintenances();
    return response.items;
  },
  // getServiceId() rather than the vessel slice: it is the same header both requests are
  // scoped by, and it is set even on the surfaces that never load a vessel into the store.
  ensureMaintenanceId: async () => ensureMaintenanceId(getServiceId()),
  createDailyLog: async (log: DailyLog) => {
    return await createDailyLog(log);
  },
  signDailyLog: async (logId: string) => {
    await signDailyLog(logId);
  },
  deleteDailyLog: async (logId: string) => {
    await deleteDailyLog(logId);
  },
  editDailyLog: async (log: DailyLog, id) => {
    await editDailyLog(log, id);
  },
  getDailyLogs: async () => {
    return getDailyLogs();
  },
  getDailyLogByDate: async (date: string) => {
    return getDailyLogByDate(date);
  },
  getDailyLogPdf: async (logId: string, timeZone?: string) => {
    return getDailyLogPdf(logId, timeZone);
  },
}));
